import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Snackbar,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { getImageUrl } from '../../utils/imageUtils';
import Profile from '../auth/Profile';
import PasswordChange from '../auth/PasswordChange';
import {
  Save as SaveIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  CloudUpload as CloudUploadIcon,
  PhotoCamera as PhotoCameraIcon,
  Group as GroupIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
};

// Initial company settings data
const initialCompanySettings = {
  name: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  phone: '',
  email: '',
  website: '',
  taxId: '',
  businessRegistrationNumber: '',
  logo: '',
};

// Mock user settings data
const mockUserSettings = {
  firstName: 'John',
  lastName: 'Doe',
  email: 'john.doe@example.com',
  phone: '(555) 987-6543',
  role: 'Administrator',
  language: 'English',
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12h',
};

// Mock notification settings
const mockNotificationSettings = {
  emailNotifications: true,
  lowStockAlerts: true,
  orderUpdates: true,
  paymentReminders: true,
  systemUpdates: false,
  dailyReports: true,
  weeklyReports: true,
  monthlyReports: true,
};

// Mock system settings
const mockSystemSettings = {
  defaultCurrency: 'USD',
  defaultTaxRate: 20,
  inventoryThreshold: 10,
  orderNumberPrefix: 'ORD',
  invoiceNumberPrefix: 'INV',
  autoBackup: true,
  backupFrequency: 'daily',
  dataRetentionPeriod: 365,
  enableAuditLog: true,
};

// Mock invoice settings
const mockInvoiceSettings = {
  invoiceNumberPrefix: 'INV',
  invoiceNumberLength: 6,
  showCompanyLogo: true,
  showTaxNumber: true,
  showBusinessRegNumber: true,
  defaultTerms: 'Payment is due within 30 days',
  defaultNotes: '',
  invoiceFooter: 'Thank you for your business!',
  dueDateDays: 30,
  reminderDaysBefore: 3,
  // Bank account details
  showBankDetails: true,
  bankName: '',
  accountName: '',
  accountNumber: '',
  routingNumber: '',
  swiftCode: '',
  iban: '',
  bsb: '',
};

// Mock security settings
const mockSecuritySettings = {
  requireStrongPasswords: true,
  passwordExpiryDays: 90,
  maxLoginAttempts: 5,
  twoFactorAuth: false,
  sessionTimeout: 30,
  ipRestriction: false,
  allowedIPs: '',
};

const Settings: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading, user, hasRole } = useAuth();
  const { settings: systemSettings, refreshSettings, updateSettings } = useSettings();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // State for each settings section
  const [companySettings, setCompanySettings] = useState(initialCompanySettings);
  const [userSettings, setUserSettings] = useState(mockUserSettings);
  const [notificationSettings, setNotificationSettings] = useState(mockNotificationSettings);
  const [localSystemSettings, setLocalSystemSettings] = useState(systemSettings);
  const [invoiceSettings, setInvoiceSettings] = useState(mockInvoiceSettings);
  const [securitySettings, setSecuritySettings] = useState(mockSecuritySettings);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // User management state
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'staff'
  });
  const [editUser, setEditUser] = useState({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    isActive: true
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [updatingUser, setUpdatingUser] = useState(false);
  const [deletingUser, setDeletingUser] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<any>(null);
  
  // Role & Permission management state
  const [roles, setRoles] = useState<any[]>([]);
  const [permissions, setPermissions] = useState<any[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [selectedRole, setSelectedRole] = useState<any>(null);
  const [rolePermissionDialogOpen, setRolePermissionDialogOpen] = useState(false);
  const [updatingRolePermissions, setUpdatingRolePermissions] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  
  useEffect(() => {
    // Fetch settings from API
    const fetchSettings = async () => {
      setLoading(true);
      try {
        // Fetch company settings and invoice settings in parallel
        const [companyResponse, invoiceSettingsResponse] = await Promise.all([
          api.get('/company'),
          api.get('/invoice-settings')
        ]);
        
        if (companyResponse.data.success) {
          // Transform snake_case to camelCase for frontend
          const companyData = companyResponse.data.data || {};
          setCompanySettings({
            name: companyData.name || '',
            address: companyData.address || '',
            city: companyData.city || '',
            state: companyData.state || '',
            zipCode: companyData.zip_code || '',
            country: companyData.country || '',
            phone: companyData.phone || '',
            email: companyData.email || '',
            website: companyData.website || '',
            taxId: companyData.tax_id || '',
            businessRegistrationNumber: companyData.business_registration_number || '',
            logo: companyData.logo || ''
          });
        }

        if (invoiceSettingsResponse.data.success) {
          const invoiceData = invoiceSettingsResponse.data.data || {};
          setInvoiceSettings({
            invoiceNumberPrefix: invoiceData.invoiceNumberPrefix || 'INV',
            invoiceNumberLength: invoiceData.invoiceNumberLength || 6,
            showCompanyLogo: invoiceData.showCompanyLogo !== undefined ? invoiceData.showCompanyLogo : true,
            showTaxNumber: invoiceData.showTaxNumber !== undefined ? invoiceData.showTaxNumber : true,
            showBusinessRegNumber: invoiceData.showBusinessRegNumber !== undefined ? invoiceData.showBusinessRegNumber : true,
            defaultTerms: invoiceData.defaultTerms || 'Payment is due within 30 days',
            defaultNotes: invoiceData.defaultNotes || '',
            invoiceFooter: invoiceData.invoiceFooter || 'Thank you for your business!',
            dueDateDays: invoiceData.dueDateDays || 30,
            reminderDaysBefore: invoiceData.reminderDaysBefore || 3,
            showBankDetails: invoiceData.showBankDetails !== undefined ? invoiceData.showBankDetails : false,
            bankName: invoiceData.bankName || '',
            accountName: invoiceData.accountName || '',
            accountNumber: invoiceData.accountNumber || '',
            routingNumber: invoiceData.routingNumber || '',
            swiftCode: invoiceData.swiftCode || '',
            iban: invoiceData.iban || '',
            bsb: invoiceData.bsb || ''
          });
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        setSnackbarMessage('Error loading settings');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };
    
    if (!authLoading && isAuthenticated) {
      fetchSettings();
    }
  }, [authLoading, isAuthenticated]);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleCompanySettingsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCompanySettings({
      ...companySettings,
      [name]: value,
    });
  };
  
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    const file = e.target.files[0];
    
    // Create immediate preview
    const previewUrl = URL.createObjectURL(file);
    setCompanySettings({
      ...companySettings,
      logo: previewUrl,
    });
    
    const formData = new FormData();
    formData.append('logo', file);
    
    setUploadingLogo(true);
    
    try {
      // Need to override default headers for file upload
      const response = await api.post('/company/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        // Clean up the temporary preview URL
        if (companySettings.logo && companySettings.logo.startsWith('blob:')) {
          URL.revokeObjectURL(companySettings.logo);
        }
        
        setCompanySettings({
          ...companySettings,
          logo: response.data.data.path,
        });
        setSnackbarMessage('Logo uploaded successfully');
      } else {
        setSnackbarMessage(response.data.message || 'Error uploading logo');
      }
      
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error uploading logo:', error);
      setSnackbarMessage('Error uploading logo');
      setSnackbarOpen(true);
    } finally {
      setUploadingLogo(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const handleUserSettingsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserSettings({
      ...userSettings,
      [name]: value,
    });
  };
  
  const handleNotificationSettingsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setNotificationSettings({
      ...notificationSettings,
      [name]: checked,
    });
  };
  
  const handleSystemSettingsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setLocalSystemSettings({
      ...localSystemSettings,
      [name]: type === 'checkbox' ? checked : value,
    });
  };
  
  const handleInvoiceSettingsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setInvoiceSettings({
      ...invoiceSettings,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSecuritySettingsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setSecuritySettings({
      ...securitySettings,
      [name]: type === 'checkbox' ? checked : value,
    });
  };
  
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      // Save company settings, system settings, and invoice settings in parallel
      const companyData = {
        name: companySettings.name,
        address: companySettings.address,
        city: companySettings.city,
        state: companySettings.state,
        zip_code: companySettings.zipCode,
        country: companySettings.country,
        phone: companySettings.phone,
        email: companySettings.email,
        website: companySettings.website,
        tax_id: companySettings.taxId,
        business_registration_number: companySettings.businessRegistrationNumber,
        logo: companySettings.logo
      };

      const invoiceSettingsData = {
        invoiceNumberPrefix: invoiceSettings.invoiceNumberPrefix,
        invoiceNumberLength: invoiceSettings.invoiceNumberLength,
        showCompanyLogo: invoiceSettings.showCompanyLogo,
        showTaxNumber: invoiceSettings.showTaxNumber,
        showBusinessRegNumber: invoiceSettings.showBusinessRegNumber,
        defaultTerms: invoiceSettings.defaultTerms,
        defaultNotes: invoiceSettings.defaultNotes,
        invoiceFooter: invoiceSettings.invoiceFooter,
        dueDateDays: invoiceSettings.dueDateDays,
        reminderDaysBefore: invoiceSettings.reminderDaysBefore,
        showBankDetails: invoiceSettings.showBankDetails,
        bankName: invoiceSettings.bankName,
        accountName: invoiceSettings.accountName,
        accountNumber: invoiceSettings.accountNumber,
        routingNumber: invoiceSettings.routingNumber,
        swiftCode: invoiceSettings.swiftCode,
        iban: invoiceSettings.iban,
        bsb: invoiceSettings.bsb
      };
      
      const [companyResponse, invoiceSettingsResponse] = await Promise.all([
        api.put('/company', companyData),
        api.put('/invoice-settings', invoiceSettingsData),
        updateSettings(localSystemSettings)
      ]);
      
      if (companyResponse.data.success && invoiceSettingsResponse.data.success) {
        setSnackbarMessage('Settings saved successfully');
      } else {
        const errorMessage = companyResponse.data.message || invoiceSettingsResponse.data.message || 'Error saving settings';
        setSnackbarMessage(errorMessage);
      }
      
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSnackbarMessage('Error saving settings');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };
  
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // User management functions
  const fetchUsers = async () => {
    if (!hasRole('super_admin')) return;
    
    setLoadingUsers(true);
    try {
      // This endpoint would need to be created in the backend
      const response = await api.get('/auth/users');
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setSnackbarMessage('Error loading users');
      setSnackbarOpen(true);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleCreateUser = async () => {
    setCreatingUser(true);
    try {
      const response = await api.post('/auth/create-user', newUser);
      if (response.data.success) {
        setUsers([...users, response.data.data]);
        setCreateUserDialogOpen(false);
        setNewUser({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          role: 'staff'
        });
        setSnackbarMessage('User created successfully');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      setSnackbarMessage(error.response?.data?.message || 'Error creating user');
      setSnackbarOpen(true);
    } finally {
      setCreatingUser(false);
    }
  };

  const handleNewUserChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewUser(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditUser = (userData: any) => {
    setSelectedUser(userData);
    setEditUser({
      id: userData.id,
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      role: userData.role,
      isActive: userData.isActive
    });
    setEditUserDialogOpen(true);
  };

  const handleEditUserChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setEditUser(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUpdateUser = async () => {
    setUpdatingUser(true);
    try {
      const response = await api.put(`/auth/users/${editUser.id}`, {
        firstName: editUser.firstName,
        lastName: editUser.lastName,
        email: editUser.email,
        role: editUser.role,
        isActive: editUser.isActive
      });
      
      if (response.data.success) {
        // Update the user in the local state
        setUsers(users.map(u => u.id === editUser.id ? response.data.data : u));
        setEditUserDialogOpen(false);
        setSelectedUser(null);
        setSnackbarMessage('User updated successfully');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      setSnackbarMessage(error.response?.data?.message || 'Error updating user');
      setSnackbarOpen(true);
    } finally {
      setUpdatingUser(false);
    }
  };

  const handleDeleteUser = (userData: any) => {
    setUserToDelete(userData);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeletingUser(userToDelete.id);
    try {
      const response = await api.delete(`/auth/users/${userToDelete.id}`);
      
      if (response.data.success) {
        // Remove user from local state
        setUsers(users.filter(u => u.id !== userToDelete.id));
        setDeleteConfirmOpen(false);
        setUserToDelete(null);
        setSnackbarMessage('User deleted successfully');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      setSnackbarMessage(error.response?.data?.message || 'Error deleting user');
      setSnackbarOpen(true);
    } finally {
      setDeletingUser(null);
    }
  };

  // Role & Permission management functions
  const fetchRoles = async () => {
    if (!hasRole('super_admin')) return;
    
    setLoadingRoles(true);
    try {
      const response = await api.get('/roles');
      if (response.data) {
        setRoles(response.data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      setSnackbarMessage('Error loading roles');
      setSnackbarOpen(true);
    } finally {
      setLoadingRoles(false);
    }
  };

  const fetchPermissions = async () => {
    if (!hasRole('super_admin')) return;
    
    setLoadingPermissions(true);
    try {
      const response = await api.get('/roles/permissions');
      if (response.data) {
        setPermissions(response.data);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setSnackbarMessage('Error loading permissions');
      setSnackbarOpen(true);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handleEditRolePermissions = (role: any) => {
    setSelectedRole(role);
    setSelectedPermissions(role.permissions.map((p: any) => p.id));
    setRolePermissionDialogOpen(true);
  };

  const handlePermissionToggle = (permissionId: number) => {
    setSelectedPermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleUpdateRolePermissions = async () => {
    if (!selectedRole) return;
    
    setUpdatingRolePermissions(true);
    try {
      const response = await api.put(`/roles/${selectedRole.id}/permissions`, {
        permissionIds: selectedPermissions
      });
      
      if (response.data) {
        // Update the role in local state
        setRoles(roles.map(role => 
          role.id === selectedRole.id 
            ? { ...role, permissions: permissions.filter(p => selectedPermissions.includes(p.id)) }
            : role
        ));
        setRolePermissionDialogOpen(false);
        setSelectedRole(null);
        setSnackbarMessage('Role permissions updated successfully');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      setSnackbarMessage(error.response?.data?.error || 'Error updating role permissions');
      setSnackbarOpen(true);
    } finally {
      setUpdatingRolePermissions(false);
    }
  };

  // Load users and roles when the user management tab is accessed and user is super admin
  useEffect(() => {
    const loadTabData = async () => {
      if (tabValue === 1 && hasRole('super_admin')) {
        try {
          await fetchUsers();
          await fetchRoles(); // Need roles for user creation/edit dropdowns
        } catch (error) {
          console.error('Error loading user management data:', error);
          // Don't block the tab switch even if data loading fails
        }
      }
    };
    loadTabData();
  }, [tabValue, hasRole]);

  // Load roles and permissions when the roles & permissions tab is accessed and user is super admin
  useEffect(() => {
    const loadTabData = async () => {
      if (tabValue === 2 && hasRole('super_admin')) {
        try {
          await fetchRoles();
          await fetchPermissions();
        } catch (error) {
          console.error('Error loading roles & permissions data:', error);
          // Don't block the tab switch even if data loading fails
        }
      }
    };
    loadTabData();
  }, [tabValue, hasRole]);

  // Sync local system settings with context
  useEffect(() => {
    setLocalSystemSettings(systemSettings);
  }, [systemSettings]);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Settings</Typography>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSaveSettings}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
      
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="settings tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            {/* For shipment_manager, only show User Profile */}
            {hasRole(['shipment_manager', 'sales_rep']) ? (
              <Tab icon={<PersonIcon />} label="User Profile" {...a11yProps(0)} />
            ) : (
              [
                <Tab key="company" icon={<BusinessIcon />} label="Company" {...a11yProps(0)} />,
                ...(hasRole('super_admin') ? [
                  <Tab key="user-management" icon={<GroupIcon />} label="User Management" {...a11yProps(1)} />,
                  <Tab key="roles-permissions" icon={<SecurityIcon />} label="Roles & Permissions" {...a11yProps(2)} />
                ] : []),
                <Tab key="user-profile" icon={<PersonIcon />} label="User Profile" {...a11yProps(hasRole('super_admin') ? 3 : 1)} />,
                <Tab key="notifications" icon={<NotificationsIcon />} label="Notifications" {...a11yProps(hasRole('super_admin') ? 4 : 2)} />,
                <Tab key="system" icon={<SettingsIcon />} label="System" {...a11yProps(hasRole('super_admin') ? 5 : 3)} />,
                <Tab key="invoice-settings" icon={<ReceiptIcon />} label="Invoice Settings" {...a11yProps(hasRole('super_admin') ? 6 : 4)} />,
                <Tab key="security" icon={<SecurityIcon />} label="Security" {...a11yProps(hasRole('super_admin') ? 7 : 5)} />
              ]
            )}
          </Tabs>
        </Box>
        
        {/* Company Settings - Hidden for shipment_manager */}
        {!hasRole(['shipment_manager', 'sales_rep']) && (
          <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Company Information
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            This information will appear on invoices, orders, and other documents.
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              src={getImageUrl(companySettings.logo)}
              alt="Company Logo"
              sx={{ width: 140, height: 140, mr: 2 }}
              variant="rounded"
            />
            <Box>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="logo-upload"
                type="file"
                onChange={handleLogoUpload}
                ref={fileInputRef}
              />
              <Button
                variant="contained"
                startIcon={uploadingLogo ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                onClick={triggerFileInput}
                disabled={uploadingLogo}
                sx={{ mb: 1 }}
              >
                {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
              </Button>
              <Typography variant="caption" display="block" color="text.secondary">
                Recommended size: 200x200 pixels (PNG, JPG, GIF)
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
            <TextField
              label="Company Name"
              name="name"
              value={companySettings.name}
              onChange={handleCompanySettingsChange}
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={companySettings.email}
              onChange={handleCompanySettingsChange}
              fullWidth
            />
            <TextField
              label="Phone"
              name="phone"
              value={companySettings.phone}
              onChange={handleCompanySettingsChange}
              fullWidth
            />
            <TextField
              label="Website"
              name="website"
              value={companySettings.website}
              onChange={handleCompanySettingsChange}
              fullWidth
            />
            <TextField
              label="Tax ID"
              name="taxId"
              value={companySettings.taxId}
              onChange={handleCompanySettingsChange}
              fullWidth
            />
            <TextField
              label="Business Registration Number"
              name="businessRegistrationNumber"
              value={companySettings.businessRegistrationNumber}
              onChange={handleCompanySettingsChange}
              fullWidth
            />
          </Box>
          
          <Typography variant="h6" gutterBottom>
            Address
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <TextField
              label="Street Address"
              name="address"
              value={companySettings.address}
              onChange={handleCompanySettingsChange}
              fullWidth
            />
            <TextField
              label="City"
              name="city"
              value={companySettings.city}
              onChange={handleCompanySettingsChange}
              fullWidth
            />
            <TextField
              label="State/Province"
              name="state"
              value={companySettings.state}
              onChange={handleCompanySettingsChange}
              fullWidth
            />
            <TextField
              label="ZIP/Postal Code"
              name="zipCode"
              value={companySettings.zipCode}
              onChange={handleCompanySettingsChange}
              fullWidth
            />
            <TextField
              label="Country"
              name="country"
              value={companySettings.country}
              onChange={handleCompanySettingsChange}
              fullWidth
            />
          </Box>
        </TabPanel>
        )}
        
        {/* User Management - Only for Super Admin */}
        {hasRole('super_admin') && (
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                User Management
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateUserDialogOpen(true)}
              >
                Add New User
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Manage system users and their permissions.
            </Typography>
            
            {loadingUsers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          {user.firstName} {user.lastName}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={user.role}
                            color={user.role === 'super_admin' ? 'error' : user.role === 'admin' ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.isActive ? 'Active' : 'Inactive'}
                            color={user.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button 
                              size="small" 
                              disabled={user.role === 'super_admin'}
                              onClick={() => handleEditUser(user)}
                            >
                              Edit
                            </Button>
                            <Button 
                              size="small" 
                              color="error"
                              disabled={user.role === 'super_admin' || deletingUser === user.id}
                              onClick={() => handleDeleteUser(user)}
                              startIcon={deletingUser === user.id ? <CircularProgress size={16} /> : <DeleteIcon />}
                            >
                              {deletingUser === user.id ? 'Deleting...' : 'Delete'}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No users found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        )}
        
        {/* Roles & Permissions Management - Only for Super Admin */}
        {hasRole('super_admin') && (
          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" gutterBottom>
              Roles & Permissions Management
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Manage system roles and assign permissions to each role.
            </Typography>
            
            {loadingRoles || loadingPermissions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Role Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Permission Count</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {roles.map((role) => (
                      <TableRow key={role.id}>
                        <TableCell>
                          <Chip
                            label={role.name}
                            color={role.name === 'super_admin' ? 'error' : role.name === 'admin' ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{role.description || 'No description'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={`${role.permissions?.length || 0} permissions`}
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="small" 
                            variant="outlined"
                            onClick={() => handleEditRolePermissions(role)}
                          >
                            Manage Permissions
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {roles.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No roles found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        )}
        
        {/* User Profile Settings */}
        <TabPanel value={tabValue} index={hasRole(['shipment_manager', 'sales_rep']) ? 0 : hasRole('super_admin') ? 3 : 1}>
          <Typography variant="h6" gutterBottom>
            Profile & Security
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Manage your personal information and security settings.
          </Typography>
          
          {/* Profile Component */}
          <Box sx={{ mb: 4 }}>
            <Profile />
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Password Change Component */}
          <Box sx={{ mb: 4 }}>
            <PasswordChange />
          </Box>
        </TabPanel>
        
        {/* Notification Settings - Hidden for shipment_manager */}
        {!hasRole(['shipment_manager', 'sales_rep']) && (
          <TabPanel value={tabValue} index={hasRole('super_admin') ? 4 : 2}>
          <Typography variant="h6" gutterBottom>
            Notification Preferences
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Configure which notifications you want to receive.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.emailNotifications}
                  onChange={handleNotificationSettingsChange}
                  name="emailNotifications"
                  color="primary"
                />
              }
              label="Enable Email Notifications"
            />
            
            <Divider />
            
            <Typography variant="subtitle1" gutterBottom>
              Inventory Alerts
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.lowStockAlerts}
                  onChange={handleNotificationSettingsChange}
                  name="lowStockAlerts"
                  color="primary"
                />
              }
              label="Low Stock Alerts"
            />
            
            <Divider />
            
            <Typography variant="subtitle1" gutterBottom>
              Order Notifications
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.orderUpdates}
                  onChange={handleNotificationSettingsChange}
                  name="orderUpdates"
                  color="primary"
                />
              }
              label="Order Updates"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.paymentReminders}
                  onChange={handleNotificationSettingsChange}
                  name="paymentReminders"
                  color="primary"
                />
              }
              label="Payment Reminders"
            />
            
            <Divider />
            
            <Typography variant="subtitle1" gutterBottom>
              System Notifications
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.systemUpdates}
                  onChange={handleNotificationSettingsChange}
                  name="systemUpdates"
                  color="primary"
                />
              }
              label="System Updates"
            />
            
            <Divider />
            
            <Typography variant="subtitle1" gutterBottom>
              Reports
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.dailyReports}
                  onChange={handleNotificationSettingsChange}
                  name="dailyReports"
                  color="primary"
                />
              }
              label="Daily Reports"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.weeklyReports}
                  onChange={handleNotificationSettingsChange}
                  name="weeklyReports"
                  color="primary"
                />
              }
              label="Weekly Reports"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.monthlyReports}
                  onChange={handleNotificationSettingsChange}
                  name="monthlyReports"
                  color="primary"
                />
              }
              label="Monthly Reports"
            />
          </Box>
        </TabPanel>
        )}
        
        {/* System Settings and remaining sections - Hidden for shipment_manager */}
        {!hasRole(['shipment_manager', 'sales_rep']) && (
          <>
            {/* System Settings */}
            <TabPanel value={tabValue} index={hasRole('super_admin') ? 5 : 3}>
          <Typography variant="h6" gutterBottom>
            System Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Configure global system settings.
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
            <TextField
              label="Default Currency"
              name="defaultCurrency"
              value={localSystemSettings.defaultCurrency}
              onChange={handleSystemSettingsChange}
              fullWidth
              select
              SelectProps={{ native: true }}
            >
              <option value="USD">USD - US Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - British Pound</option>
              <option value="JPY">JPY - Japanese Yen</option>
              <option value="CAD">CAD - Canadian Dollar</option>
              <option value="AUD">AUD - Australian Dollar</option>
              <option value="INR">INR - Indian Rupee</option>
            </TextField>
            
            <TextField
              label="Default Tax Rate (%)"
              name="defaultTaxRate"
              type="number"
              value={localSystemSettings.defaultTaxRate}
              onChange={handleSystemSettingsChange}
              fullWidth
              InputProps={{ inputProps: { min: 0, max: 100 } }}
            />
            
            <TextField
              label="Low Inventory Threshold"
              name="inventoryThreshold"
              type="number"
              value={localSystemSettings.inventoryThreshold}
              onChange={handleSystemSettingsChange}
              fullWidth
              InputProps={{ inputProps: { min: 0 } }}
            />
            
            <TextField
              label="Order Number Prefix"
              name="orderNumberPrefix"
              value={localSystemSettings.orderNumberPrefix}
              onChange={handleSystemSettingsChange}
              fullWidth
            />
            
            <TextField
              label="Invoice Number Prefix"
              name="invoiceNumberPrefix"
              value={localSystemSettings.invoiceNumberPrefix}
              onChange={handleSystemSettingsChange}
              fullWidth
            />
          </Box>
          
          <Typography variant="h6" gutterBottom>
            Data Management
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={localSystemSettings.autoBackup}
                    onChange={handleSystemSettingsChange}
                    name="autoBackup"
                    color="primary"
                  />
                }
                label="Automatic Backups"
              />
            </Box>
            
            {localSystemSettings.autoBackup && (
              <TextField
                label="Backup Frequency"
                name="backupFrequency"
                value={localSystemSettings.backupFrequency}
                onChange={handleSystemSettingsChange}
                fullWidth
                select
                SelectProps={{ native: true }}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </TextField>
            )}
            
            <TextField
              label="Data Retention Period (days)"
              name="dataRetentionPeriod"
              type="number"
              value={localSystemSettings.dataRetentionPeriod}
              onChange={handleSystemSettingsChange}
              fullWidth
              InputProps={{ inputProps: { min: 30 } }}
            />
            
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={localSystemSettings.enableAuditLog}
                    onChange={handleSystemSettingsChange}
                    name="enableAuditLog"
                    color="primary"
                  />
                }
                label="Enable Audit Log"
              />
            </Box>
          </Box>
          
          <Box sx={{ mt: 3 }}>
            <Button variant="outlined" color="error" sx={{ mr: 2 }}>
              Clear All Data
            </Button>
            <Button variant="outlined">
              Export All Data
            </Button>
          </Box>
        </TabPanel>
        
        {/* Invoice Settings */}
        <TabPanel value={tabValue} index={hasRole('super_admin') ? 6 : 4}>
          <Typography variant="h6" gutterBottom>
            Invoice Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Configure invoice generation and display settings.
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
            Invoice Numbering
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
            <TextField
              label="Invoice Number Prefix"
              name="invoiceNumberPrefix"
              value={invoiceSettings.invoiceNumberPrefix}
              onChange={handleInvoiceSettingsChange}
              fullWidth
              helperText="e.g., INV"
            />
            
            <TextField
              label="Invoice Number Length"
              name="invoiceNumberLength"
              type="number"
              value={invoiceSettings.invoiceNumberLength}
              onChange={handleInvoiceSettingsChange}
              fullWidth
              InputProps={{ inputProps: { min: 4, max: 10 } }}
              helperText="Number of digits (4-10)"
            />
          </Box>
          
          <Typography variant="subtitle1" gutterBottom>
            Invoice Display
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={invoiceSettings.showCompanyLogo}
                  onChange={handleInvoiceSettingsChange}
                  name="showCompanyLogo"
                  color="primary"
                />
              }
              label="Show Company Logo on Invoice"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={invoiceSettings.showTaxNumber}
                  onChange={handleInvoiceSettingsChange}
                  name="showTaxNumber"
                  color="primary"
                />
              }
              label="Show Tax Number on Invoice"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={invoiceSettings.showBusinessRegNumber}
                  onChange={handleInvoiceSettingsChange}
                  name="showBusinessRegNumber"
                  color="primary"
                />
              }
              label="Show Business Registration Number on Invoice"
            />
          </Box>
          
          <Typography variant="subtitle1" gutterBottom>
            Default Invoice Content
          </Typography>
          
          <Box sx={{ display: 'grid', gap: 3, mb: 3 }}>
            <TextField
              label="Default Terms & Conditions"
              name="defaultTerms"
              value={invoiceSettings.defaultTerms}
              onChange={handleInvoiceSettingsChange}
              fullWidth
              multiline
              rows={3}
              helperText="Default terms that appear on all invoices"
            />
            
            <TextField
              label="Default Notes"
              name="defaultNotes"
              value={invoiceSettings.defaultNotes}
              onChange={handleInvoiceSettingsChange}
              fullWidth
              multiline
              rows={2}
              helperText="Default notes for invoices (optional)"
            />
            
            <TextField
              label="Invoice Footer"
              name="invoiceFooter"
              value={invoiceSettings.invoiceFooter}
              onChange={handleInvoiceSettingsChange}
              fullWidth
              helperText="Text that appears at the bottom of invoices"
            />
          </Box>
          
          <Typography variant="subtitle1" gutterBottom>
            Payment Settings
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
            <TextField
              label="Default Due Date (Days)"
              name="dueDateDays"
              type="number"
              value={invoiceSettings.dueDateDays}
              onChange={handleInvoiceSettingsChange}
              fullWidth
              InputProps={{ inputProps: { min: 1, max: 365 } }}
              helperText="Days from invoice date"
            />
            
            <TextField
              label="Payment Reminder (Days Before Due)"
              name="reminderDaysBefore"
              type="number"
              value={invoiceSettings.reminderDaysBefore}
              onChange={handleInvoiceSettingsChange}
              fullWidth
              InputProps={{ inputProps: { min: 1, max: 30 } }}
            />
          </Box>
          
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
            Bank Account Details
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Add bank account information to display on invoices for customer payments.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={invoiceSettings.showBankDetails}
                  onChange={handleInvoiceSettingsChange}
                  name="showBankDetails"
                  color="primary"
                />
              }
              label="Show Bank Details on Invoice"
            />
          </Box>
          
          {invoiceSettings.showBankDetails && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
              <TextField
                label="Bank Name"
                name="bankName"
                value={invoiceSettings.bankName}
                onChange={handleInvoiceSettingsChange}
                fullWidth
                helperText="Name of your bank"
              />
              
              <TextField
                label="Account Name"
                name="accountName"
                value={invoiceSettings.accountName}
                onChange={handleInvoiceSettingsChange}
                fullWidth
                helperText="Account holder name"
              />
              
              <TextField
                label="Account Number"
                name="accountNumber"
                value={invoiceSettings.accountNumber}
                onChange={handleInvoiceSettingsChange}
                fullWidth
                helperText="Bank account number"
              />
              
              <TextField
                label="Routing Number / Sort Code"
                name="routingNumber"
                value={invoiceSettings.routingNumber}
                onChange={handleInvoiceSettingsChange}
                fullWidth
                helperText="US: Routing Number, UK: Sort Code"
              />
              
              <TextField
                label="SWIFT/BIC Code"
                name="swiftCode"
                value={invoiceSettings.swiftCode}
                onChange={handleInvoiceSettingsChange}
                fullWidth
                helperText="For international transfers"
              />
              
              <TextField
                label="IBAN"
                name="iban"
                value={invoiceSettings.iban}
                onChange={handleInvoiceSettingsChange}
                fullWidth
                helperText="International Bank Account Number"
              />
              
              <TextField
                label="BSB (Australia)"
                name="bsb"
                value={invoiceSettings.bsb}
                onChange={handleInvoiceSettingsChange}
                fullWidth
                helperText="Bank State Branch (Australia only)"
              />
            </Box>
          )}
          
        </TabPanel>
        
        {/* Security Settings */}
        <TabPanel value={tabValue} index={hasRole('super_admin') ? 7 : 5}>
          <Typography variant="h6" gutterBottom>
            Security Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Configure security settings for your system.
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={securitySettings.requireStrongPasswords}
                    onChange={handleSecuritySettingsChange}
                    name="requireStrongPasswords"
                    color="primary"
                  />
                }
                label="Require Strong Passwords"
              />
            </Box>
            
            <TextField
              label="Password Expiry (days)"
              name="passwordExpiryDays"
              type="number"
              value={securitySettings.passwordExpiryDays}
              onChange={handleSecuritySettingsChange}
              fullWidth
              InputProps={{ inputProps: { min: 0 } }}
              helperText="0 = never expire"
            />
            
            <TextField
              label="Max Login Attempts"
              name="maxLoginAttempts"
              type="number"
              value={securitySettings.maxLoginAttempts}
              onChange={handleSecuritySettingsChange}
              fullWidth
              InputProps={{ inputProps: { min: 1 } }}
            />
            
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={securitySettings.twoFactorAuth}
                    onChange={handleSecuritySettingsChange}
                    name="twoFactorAuth"
                    color="primary"
                  />
                }
                label="Two-Factor Authentication"
              />
            </Box>
            
            <TextField
              label="Session Timeout (minutes)"
              name="sessionTimeout"
              type="number"
              value={securitySettings.sessionTimeout}
              onChange={handleSecuritySettingsChange}
              fullWidth
              InputProps={{ inputProps: { min: 1 } }}
            />
            
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={securitySettings.ipRestriction}
                    onChange={handleSecuritySettingsChange}
                    name="ipRestriction"
                    color="primary"
                  />
                }
                label="IP Address Restriction"
              />
            </Box>
            
            {securitySettings.ipRestriction && (
              <TextField
                label="Allowed IP Addresses"
                name="allowedIPs"
                value={securitySettings.allowedIPs}
                onChange={handleSecuritySettingsChange}
                fullWidth
                helperText="Comma-separated list of IP addresses"
              />
            )}
          </Box>
          
          <Alert severity="info" sx={{ mt: 3 }}>
            For security reasons, some settings may require administrator approval before taking effect.
          </Alert>
        </TabPanel>
          </>
        )}
      </Paper>
      
      {/* Create User Dialog */}
      <Dialog open={createUserDialogOpen} onClose={() => setCreateUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            <TextField
              label="First Name"
              name="firstName"
              value={newUser.firstName}
              onChange={handleNewUserChange}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              name="lastName"
              value={newUser.lastName}
              onChange={handleNewUserChange}
              fullWidth
              required
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={newUser.email}
              onChange={handleNewUserChange}
              fullWidth
              required
            />
            <TextField
              label="Password"
              name="password"
              type="password"
              value={newUser.password}
              onChange={handleNewUserChange}
              fullWidth
              required
              helperText="Password must be at least 8 characters with uppercase, lowercase, number, and special character"
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                name="role"
                value={newUser.role}
                label="Role"
                onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value }))}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.name}>
                    {role.name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateUserDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={creatingUser || !newUser.firstName || !newUser.lastName || !newUser.email || !newUser.password}
          >
            {creatingUser ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit User Dialog */}
      <Dialog open={editUserDialogOpen} onClose={() => setEditUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            <TextField
              label="First Name"
              name="firstName"
              value={editUser.firstName}
              onChange={handleEditUserChange}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              name="lastName"
              value={editUser.lastName}
              onChange={handleEditUserChange}
              fullWidth
              required
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={editUser.email}
              onChange={handleEditUserChange}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                name="role"
                value={editUser.role}
                label="Role"
                onChange={(e) => setEditUser(prev => ({ ...prev, role: e.target.value }))}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.name}>
                    {role.name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={editUser.isActive}
                  onChange={handleEditUserChange}
                  name="isActive"
                  color="primary"
                />
              }
              label="Active User"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUserDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateUser}
            variant="contained"
            disabled={updatingUser || !editUser.firstName || !editUser.lastName || !editUser.email}
          >
            {updatingUser ? 'Updating...' : 'Update User'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user <strong>{userToDelete?.firstName} {userToDelete?.lastName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. All data associated with this user will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDeleteUser}
            color="error"
            variant="contained"
            disabled={!!deletingUser}
          >
            {deletingUser ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Role Permission Management Dialog */}
      <Dialog 
        open={rolePermissionDialogOpen} 
        onClose={() => setRolePermissionDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Manage Permissions for {selectedRole?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which permissions this role should have. Permissions are grouped by resource.
          </Typography>
          
          {permissions.length > 0 ? (
            <Box>
              {/* Group permissions by resource */}
              {Object.entries(
                permissions.reduce((acc: any, permission: any) => {
                  const resource = permission.resource;
                  if (!acc[resource]) {
                    acc[resource] = [];
                  }
                  acc[resource].push(permission);
                  return acc;
                }, {})
              ).map(([resource, resourcePermissions]: [string, any]) => (
                <Box key={resource} sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ textTransform: 'capitalize' }}>
                    {resource}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1 }}>
                    {resourcePermissions.map((permission: any) => (
                      <FormControlLabel
                        key={permission.id}
                        control={
                          <Switch
                            checked={selectedPermissions.includes(permission.id)}
                            onChange={() => handlePermissionToggle(permission.id)}
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {permission.action}
                            </Typography>
                            {permission.description && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {permission.description}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    ))}
                  </Box>
                  <Divider sx={{ mt: 2 }} />
                </Box>
              ))}
            </Box>
          ) : (
            <Typography>No permissions available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRolePermissionDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateRolePermissions}
            variant="contained"
            disabled={updatingRolePermissions}
          >
            {updatingRolePermissions ? 'Updating...' : 'Update Permissions'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default Settings;
